/**
 * AuthFormPanel — the shared RIGHT-hand form panel for every unauthenticated
 * entry surface (LoginPage, AuthLayout's forgot/reset flows, InviteAcceptPage).
 *
 * Design intent (the "Threshold" redesign, 2026-07-09): this panel deliberately
 * IS the product. The night-sky <AuthMarketingPanel /> on the left carries the
 * landing-page world; this panel carries the app the user is about to enter —
 * the exact `bg-surface` ground (#F4F5F7) and a white `.card`-style container
 * (bg-card / rounded / border-line / shadow-card) that every authenticated
 * screen is built from. So signing in reads as a doorway from marketing → the
 * product, not a jump to a third unrelated look.
 *
 * It replaced the pale-mint gradient (from-brand-accent-light → -primary-light)
 * that used to live in three duplicated `<main>` blocks and appeared nowhere
 * else in the app. Extracting it to one component means the auth form side can
 * never drift between those surfaces again (Eng Principle #3).
 *
 * `children` render inside the white card; the optional `footer` renders below
 * the card, quiet, directly on the light ground (status/contact/privacy links).
 */

import { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Quiet links rendered under the card, on the light ground. Optional. */
  footer?: ReactNode;
}

export default function AuthFormPanel({ children, footer }: Props) {
  return (
    // The panel is the scroll container; the inner `min-h-full` wrapper does the
    // vertical placement: it CENTERS the card when the form fits the viewport
    // (the premium look), and TOP-ANCHORS it when the form is taller than the
    // screen — so the form header + first fields are always immediately in view
    // on arrival, never landed mid-scroll with the title pushed off-screen.
    <main className="bg-surface overflow-y-auto">
      <div className="min-h-full flex flex-col justify-center px-6 py-8 sm:px-10 lg:px-14">
        <div className="w-full max-w-[440px] mx-auto">
          <div className="bg-card rounded-2xl border border-line shadow-card p-6 sm:p-8">
            {children}
          </div>
          {footer && <div className="mt-5">{footer}</div>}
        </div>
      </div>
    </main>
  );
}
